import React, { useContext, useMemo } from 'react';

//mui imports====
import { Box } from '@mui/system';
import useStyles from './styles';
import { Grid, TextField, Typography } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DropDownSingle from '../DropDownSingle';
//=========
import { ACCESS_CONTROL_CONSTANTS } from '../../core/constants';
import { UserContext } from '../../Contexts/UserContext';
const { SYSTEMS, UI_ACCESS, TYPES } = ACCESS_CONTROL_CONSTANTS;

/**
 * @name ArrayInputControlledConfig
 * @description This Component can be used to display an array which has multiple inputs. Inputs can be added, updated and deleted with direct value functionality.
 * @param {object} directValue(required) - direct array of object can be passed in this component, but it has a default schema
 *  default schema :-  [{ value:'', enabled: true or false }]
 * @param {object} updateDirectValue(required) - this function can be used to directly update new array of object value
 * @param {string} label(optional) - this function can be used to directly update new array of object value
 * @param {string} inputContainerStyle(optional) - it is an optional container style
 * @param {string} inputGridStyle(optional) - it is an optional grid style
 * @param {string} keyData (optional) since not yet functional like the other input components, Needs to be integrated like that too , Can be keyData if its
 * a video section property or any other string keyData
 * That keyData must match with the controlled access rule identifier passed to be effective.
 * Uses : Currently it is only used in controlled access ,wheather to disable/hide this component for the particular access rule.
 */
const ArrayInputControlledConfig = ({
	directValue,
	updateDirectValue,
	label,
	inputContainerStyle,
	inputGridStyle,
	dropDownOptions,
	useDropDownSingleMenu = false,
	keyData = '',
	system = SYSTEMS.FRAMERATE
}) => {
	const classes = useStyles();
	const { getAccessControlSet } = useContext(UserContext);

	/**
	 * This variable contains a set of strings that signifies if this input is either visible/disabled/hidden
	 * @type {Set<string>}
	 */
	const arrayInputControlledAccessSet = useMemo(() => {
		return getAccessControlSet(system, TYPES.INPUT_NAMES, keyData);
	}, [getAccessControlSet, keyData, system]);

	const inputArray = directValue ? directValue : [];

	/**
	 * @name addNewElement
	 * @description This function can be used to add a new element to array, it has a default schema which is added whenever a element is added
	 */
	const addNewElement = () => {
		let deepCloneArray = structuredClone(inputArray);
		let updatedArray = [...deepCloneArray, { value: '', editable: true }];
		if (inputArray && Array.isArray(inputArray)) {
			updatedArray = structuredClone(updatedArray);
			updateDirectValue(updatedArray);
		} else alert("Could'nt find inputArray in rawConfig!");
	};

	/**
	 * @name updateArrayElement
	 * @description This function can be used to update an element in the array
	 * @param {number} id - it is the id of the particular array element which is to be updated
	 * @param {string} updatedValue - it is the updated value  which is to be updated over the particular array element
	 */
	const updateArrayElement = (id, updatedValue) => {
		if (inputArray && Array.isArray(inputArray)) {
			let updatedArray = structuredClone(inputArray);
			updatedArray[id].value = updatedValue;
			updateDirectValue(updatedArray);
		} else alert("Could'nt find inputArray in rawConfig!");
	};

	/**
	 * @name deleteArrayElement
	 * @description This function can be used to delete an element from array
	 * @param {number} arrayIndex - it is the id of the particular array element which is to be deleted
	 */
	const deleteArrayElement = (arrayIndex) => {
		if (inputArray && Array.isArray(inputArray)) {
			let updatedArray = structuredClone(inputArray);
			updatedArray = updatedArray.filter((data, id) => id !== arrayIndex);

			updateDirectValue(updatedArray);
		} else alert("Could'nt find inputArray in rawConfig!");
	};

	const listItemsNotInUse = useMemo(() => {
		if (!dropDownOptions) return [];
		return dropDownOptions.filter(
			(item) => !inputArray.find((element) => element.value === item.value)
		);
	}, [dropDownOptions, inputArray]);

	/**
	 * The add new button should be shown only if
	 * 1. It is not disabled
	 * 2. If it is a dropdown menu, then it should be shown only if there are items not in use
	 */
	const showAddButton =
		!arrayInputControlledAccessSet.has(UI_ACCESS.DISABLE) &&
		(!useDropDownSingleMenu || listItemsNotInUse.length > 0);

	return (
		<>
			{!arrayInputControlledAccessSet.has(UI_ACCESS.HIDE) && (
				<>
					<Box sx={{ marginTop: '3rem', display: 'flex' }} spacing={4}>
						<Typography className={classes.heading} fontWeight={600} variant="h6">
							{label || ''}
						</Typography>
						{showAddButton && (
							<AddCircleOutlineOutlinedIcon
								color="primary"
								fontSize="large"
								className={classes.icon}
								onClick={addNewElement}
								style={{ marginLeft: '1rem' }}
							/>
						)}
					</Box>
					<Grid
						container
						style={inputContainerStyle || {}}
						marginTop="2rem"
						columnSpacing={1.5}
						rowGap={1.5}
					>
						{inputArray.map((inputArrayObject, id) => {
							return (
								<SingleArrayElement
									key={id}
									id={id}
									arrayElement={inputArrayObject?.value}
									updateArrayElement={updateArrayElement}
									deleteArrayElement={deleteArrayElement}
									editable={
										typeof inputArrayObject?.editable === 'boolean'
											? inputArrayObject?.editable
											: arrayInputControlledAccessSet.has(UI_ACCESS.VISIBLE)
									}
									inputGridStyle={inputGridStyle || {}}
									dropDownOptions={dropDownOptions}
									useDropDownSingleMenu={useDropDownSingleMenu}
								/>
							);
						})}
					</Grid>
				</>
			)}
		</>
	);
};

const SingleArrayElement = ({
	id,
	arrayElement,
	updateArrayElement,
	deleteArrayElement,
	editable,
	inputGridStyle,
	dropDownOptions,
	useDropDownSingleMenu
}) => {
	const classes = useStyles();

	return (
		<Grid item xs={3} style={inputGridStyle}>
			{useDropDownSingleMenu && (
				<DropDownSingle
					list={dropDownOptions}
					isObjectEditor={false}
					directValue={arrayElement}
					updateDirectValue={(value) => {
						updateArrayElement(id, value);
					}}
					formControlStyles={{ width: '100%' }}
					formControlVariant={'outlined'}
					containerStyles={{ width: '100%' }}
					disabled={editable === false}
				/>
			)}
			{!useDropDownSingleMenu && (
				<TextField
					id="outlined-size-normal-arrayElement"
					value={arrayElement || ''}
					style={{ width: '80%' }}
					onChange={(e) => {
						updateArrayElement(id, e.target.value);
					}}
					disabled={editable === false}
				/>
			)}
			{editable && (
				<Box style={{ display: 'flex', alignItems: 'center', marginLeft: '.5rem' }}>
					<DeleteOutlinedIcon
						color="primary"
						onClick={() => deleteArrayElement(id)}
						className={classes.icon}
					/>
				</Box>
			)}
		</Grid>
	);
};

export default ArrayInputControlledConfig;
